import React from 'react';
import { Trans, t } from '@lingui/macro';
import { Button, Field, Input, Spinner } from '@economic/taco';

import { useCurrentAgreement } from '@economic/react-app';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import { eInvoiceActivationEndpoints } from '~/app/pages/settings/company/electronic-invoice-reception/tabs/einvoice/constants';
import ValidationInformation from '~/app/pages/settings/company/electronic-invoice-reception/components/ValidationInformation';
import validateCompanyForReception, {
    ValidationStatus,
} from '~/app/pages/settings/company/electronic-invoice-reception/components/CompanyValidator';

import EInvoice from './onboarding_einvoice.svg';
import EInvoiceSuccess from './onboarding_einvoice_success.svg';

import { OnboardingStatusCompanyData } from './types';

interface Props {
    company: OnboardingStatusCompanyData;
    onDone: () => void;
    onBack: () => void;
    onSkip: () => void;
    children: string | JSX.Element;
}

export default function StepEInvoice({ company, onDone, onBack, onSkip, children }: Props) {
    const agreement = useCurrentAgreement();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [isActivated, setIsActivated] = React.useState<boolean>(false);
    const [activateLoading, setActivateLoading] = React.useState<boolean>(false);
    const [newCvr, setNewCvr] = React.useState<string>('');
    const [validCvr, setValidCvr] = React.useState<boolean>(false);
    const [cvrError, setCvrError] = React.useState<string>();
    const [cvrValidationStatus, setCvrValidationStatus] = React.useState<ValidationStatus>();

    React.useEffect(() => {
        handleGetStatus();
    }, []);

    const handleGetStatus = async () => {
        try {
            setIsLoading(true);
            const getCompanyResponse = await internalApiClient.get(eInvoiceActivationEndpoints.getCurrentCompany);
            if (getCompanyResponse.data?.model?.cvr) {
                setNewCvr(getCompanyResponse.data.model.cvr);
            } else {
                setNewCvr('');
            }
            const response = await internalApiClient.get(eInvoiceActivationEndpoints.getStatus);
            if (response.status === 200 && response.data) {
                setIsActivated(response.data.isActive);
            } else {
                setIsActivated(false);
            }
            setIsLoading(false);
        } catch {
            setIsActivated(false);
            setIsLoading(false);
        }
    };

    const isValidCvr = (cvr: string) => /^[0-9]{8}$/.test(cvr);

    const verifyCVRNumber = async (cvr: string) => {
        try {
            const registerGln = false;
            const ignoreInUse = false;
            const validationResult = await validateCompanyForReception(cvr, agreement.name, registerGln, ignoreInUse);
            if (validationResult) {
                setCvrValidationStatus(validationResult.status);
                return (
                    validationResult.status === ValidationStatus.Valid || validationResult.status === ValidationStatus.Mismatch
                );
            }
            return false;
        } catch {
            return false;
        }
    };

    const handleOnChange = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const cvr = ev.currentTarget.value;
        setNewCvr(cvr);
        if (isValidCvr(cvr)) {
            setValidCvr(true);
            const isVerified = await verifyCVRNumber(cvr);
            if (isVerified) {
                setCvrError(undefined);
            } else {
                setCvrError(t`CVR validation failed.`);
            }
        } else {
            setValidCvr(false);
            setCvrError(t`Invalid CVR`);
        }
    };

    const handleOnActivate = async () => {
        try {
            setActivateLoading(true);
            const isVerified = await verifyCVRNumber(newCvr);
            if (isVerified) {
                const getCompanyResponse = await internalApiClient.get(eInvoiceActivationEndpoints.getCurrentCompany);
                await internalApiClient.post(eInvoiceActivationEndpoints.updateCurrentCompany, {
                    ...getCompanyResponse.data.model,
                    cvr: newCvr,
                });

                const response = await internalApiClient.post(eInvoiceActivationEndpoints.activate);
                if (response.status === 201) {
                    await handleGetStatus();
                } else {
                    setCvrError(t`Failed to activate E-invoicing.`);
                    setActivateLoading(false);
                }
            } else {
                setCvrError(t`CVR validation failed.`);
                setActivateLoading(false);
            }
        } catch {
            setActivateLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="onboarding__container__fixed-size">
                <div className="onboarding onboarding__e-invoicing">
                    <Spinner />
                </div>
            </div>
        );
    }
    if (!isActivated) {
        return (
            <div className={validCvr && cvrValidationStatus ? '' : 'onboarding__container__fixed-size'}>
                <div className="onboarding onboarding__e-invoicing text-center spa">
                    <div className="flex flex-col">
                        <div className="mb-12">{children}</div>
                        <div className="mb-4">
                            <img src={EInvoice} className="w-64 mx-auto" />
                        </div>
                        <h2 className="mb-4">
                            <Trans>Avoid errors and manual work with e-invoicing</Trans>
                        </h2>
                        <p className="mb-4">
                            <Trans>
                                Electronic invoicing is an <strong>automated way of processing invoices</strong>,<br /> which you
                                e.g. must use if you have public customers.
                            </Trans>
                        </p>
                        <p className="mb-4">
                            <Trans>
                                Regardless of which customers you have, <strong>we recommend that you use e-invoicing</strong>. It
                                saves you from tedious, manual work, expensive typos and much more.
                            </Trans>
                        </p>
                        <p className="mb-4">
                            <Trans>
                                <strong>E-invoicing is free in e-conomic</strong> and can be activated with your CVR-number right
                                here.
                            </Trans>
                        </p>
                        <div className="w-full flex flex-col items-center mb-2">
                            <div className="relative inline-flex w-48 mb-2">
                                <strong>
                                    <Trans>Insert CVR-number</Trans>
                                </strong>
                            </div>
                            <div className="w-48">
                                <Input
                                    aria-label={t`Insert CVR-number`}
                                    name="cvr"
                                    maxLength={8}
                                    pattern="[0-9]{8}"
                                    invalid={cvrError !== undefined}
                                    onChange={handleOnChange}
                                    value={newCvr}
                                />
                            </div>
                        </div>
                        <div className="w-full flex flex-col items-center mb-4">
                            {cvrError && (
                                <div className="text-sm red text-red font-light" data-test-id="InputError">
                                    {cvrError}
                                </div>
                            )}
                            {validCvr && cvrValidationStatus && (
                                <div className="m-4">
                                    <ValidationInformation
                                        loading={activateLoading}
                                        validationStatus={cvrValidationStatus}
                                        companyName={company.name}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="onboarding__navigation-buttons">
                            <div className="onboarding__skip-step">
                                <Button
                                    appearance="discrete"
                                    onClick={e => {
                                        e.preventDefault();
                                        onSkip();
                                    }}>
                                    <Trans>Skip</Trans>
                                </Button>
                            </div>

                            <Button appearance="primary" disabled={activateLoading} onClick={handleOnActivate}>
                                <Trans>Activate e-invoicing</Trans>
                            </Button>

                            <Button
                                appearance="discrete"
                                onClick={e => {
                                    e.preventDefault();
                                    onBack();
                                }}>
                                <Trans>Back</Trans>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="mb-4">
            {children}
            <div className="mb-8">
                <div className="mt-8 mb-4 mx-auto">
                    <img className="mx-auto" src={EInvoiceSuccess} alt="" />
                </div>
                <h3 className="text-center mb-4">
                    <Trans>Great! E-Invocing Activated</Trans>
                </h3>
                <Field>
                    <div className="w-1/2 mx-auto h-9 flex gap-5 flex-start border border-solid border-[#ACACAC] text-[#1C1C1C] rounded">
                        <div className="text-sm font-bold p-2">
                            <Trans>CVR-number</Trans>
                        </div>
                        <div className="text-sm font-normal p-2">{newCvr}</div>
                    </div>
                </Field>
                <div className="text-center mb-4">
                    <Trans>
                        You are now ready to receive e-invoices directly in your Inbox.
                        <br /> You can always manage your activation under 'all settings'.
                    </Trans>
                </div>
            </div>
            <div className="onboarding__navigation-buttons m-4">
                <Button appearance="primary" onClick={onDone}>
                    <Trans>Continue</Trans>
                </Button>
            </div>
        </div>
    );
}
